import { API_BASE_URL, endpoints } from "./config";
import { authHeadersForGet, authHeadersJson } from "./http";
import { Client, ClientType, CreateClientDto } from "../types/client";
import { Sale } from "../types/sale";

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  lastPage: number;
}

function mapClientFromApi(raw: Record<string, unknown>): Client {
  const created = raw.createdAt ?? raw.created_at;
  const updated = raw.updatedAt ?? raw.updated_at;
  return {
    reference: String(raw.reference ?? ""),
    name: String(raw.name ?? ""),
    phoneNumber:
      raw.phoneNumber != null && raw.phoneNumber !== ""
        ? String(raw.phoneNumber)
        : undefined,
    clientType:
      raw.clientType != null
        ? (String(raw.clientType) as ClientType)
        : raw.client_type != null
        ? (String(raw.client_type) as ClientType)
        : undefined,
    createdAt: (created != null ? created : "") as Client["createdAt"],
    updatedAt: (updated != null ? updated : "") as Client["updatedAt"],
  };
}

export const clientsApi = {
  getAll: async (
    page: number = 1,
    limit: number = 10,
    clientType?: ClientType,
  ): Promise<PaginatedResponse<Client>> => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (clientType) params.set('clientType', clientType);
      const response = await fetch(
        `${API_BASE_URL}${endpoints.clients.getAll}?${params.toString()}`,
        { headers: authHeadersForGet() }
      );
      if (!response.ok) {
        throw new Error("Error al obtener los clientes");
      }
      const body = (await response.json()) as PaginatedResponse<Record<string, unknown>>;
      return {
        ...body,
        data: Array.isArray(body.data)
          ? body.data.map((row) => mapClientFromApi(row))
          : [],
      };
    } catch (error) {
      console.error("Error en API de clientes:", error);
      throw error;
    }
  },

  search: async (term: string): Promise<Client[]> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${endpoints.clients.search(term)}`,
        { headers: authHeadersForGet() }
      );
      if (!response.ok) {
        throw new Error("Error al buscar clientes");
      }
      const rows = (await response.json()) as unknown;
      return Array.isArray(rows)
        ? rows.map((row) => mapClientFromApi(row as Record<string, unknown>))
        : [];
    } catch (error) {
      console.error("Error en búsqueda de clientes:", error);
      return [];
    }
  },

  getOne: async (reference: string): Promise<Client> => {
    const response = await fetch(
      `${API_BASE_URL}${endpoints.clients.getOne(reference)}`,
      { headers: authHeadersForGet() }
    );
    const raw = (await response.json()) as Record<string, unknown>;
    if (!response.ok) {
      throw new Error(
        typeof raw.message === "string" ? raw.message : "Cliente no encontrado"
      );
    }
    return mapClientFromApi(raw);
  },

  create: async (client: CreateClientDto): Promise<Client> => {
    const response = await fetch(`${API_BASE_URL}${endpoints.clients.create}`, {
      method: "POST",
      headers: authHeadersJson(),
      body: JSON.stringify(client),
    });
    const raw = (await response.json()) as Record<string, unknown>;
    return mapClientFromApi(raw);
  },

  update: async (
    reference: string,
    client: Partial<CreateClientDto>
  ): Promise<Client> => {
    const response = await fetch(
      `${API_BASE_URL}${endpoints.clients.update(reference)}`,
      {
        method: "PUT",
        headers: authHeadersJson(),
        body: JSON.stringify(client),
      }
    );
    const raw = (await response.json()) as Record<string, unknown>;
    return mapClientFromApi(raw);
  },

  delete: async (reference: string): Promise<boolean> => {
    const response = await fetch(
      `${API_BASE_URL}${endpoints.clients.delete(reference)}`,
      {
        method: "DELETE",
        headers: authHeadersForGet(),
      }
    );
    return response.ok;
  },

  getSales: async (clientReference: string): Promise<Sale[]> => {
    const response = await fetch(
      `${API_BASE_URL}${endpoints.clients.getSales(clientReference)}`,
      { headers: authHeadersForGet() }
    );
    return response.json();
  },
};
